import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Camera, Loader2, X } from 'lucide-react';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import type { OnboardingFormReturn } from '../useOnboardingForm';

export function ProfilePhotoStep({ form }: { form: OnboardingFormReturn }) {
  const { profilePreview, profileFile, profileInputRef, handleProfileFileChange, clearProfileFile, processingImage } =
    form;
  return (
    <Flex direction="column" align="center" gap="6">
      <Text fontSize="md" color="volyx.textSecondary" textAlign="center">
        Pick a profile picture so other volunteers and organizations recognize you. You can do this later.
      </Text>
      <Box position="relative">
        <chakra.img
          src={profilePreview || DEFAULT_PROFILE_IMAGE}
          alt="Profile preview"
          w="40"
          h="40"
          borderRadius="full"
          objectFit="cover"
          border="4px solid"
          borderColor="volyx.borderLight"
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const t = e.currentTarget;
            if (t.src !== window.location.origin + DEFAULT_PROFILE_IMAGE && !t.src.endsWith(DEFAULT_PROFILE_IMAGE)) {
              t.src = DEFAULT_PROFILE_IMAGE;
            }
          }}
        />
        <chakra.button
          position="absolute"
          bottom="0"
          right="0"
          w="12"
          h="12"
          bg="volyx.action"
          color="white"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="4px solid"
          borderColor="white"
          cursor="pointer"
          transition="transform 0.2s"
          _hover={{ transform: 'scale(1.05)' }}
          onClick={() => profileInputRef.current?.click()}
          disabled={processingImage}
          aria-label={processingImage ? 'Processing image' : 'Pick profile image'}
        >
          {processingImage ? (
            <chakra.span animation="spin 1s linear infinite" display="flex">
              <Loader2 size={22} />
            </chakra.span>
          ) : (
            <Camera size={22} />
          )}
        </chakra.button>
        <input
          ref={profileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleProfileFileChange}
        />
      </Box>
      {processingImage ? (
        <Text fontSize="sm" color="volyx.textSecondary">
          Processing image...
        </Text>
      ) : profileFile ? (
        <Flex align="center" gap="2">
          <Text fontSize="sm" color="volyx.textSecondary" lineClamp={1} maxW="240px">
            {profileFile.name}
          </Text>
          <chakra.button
            type="button"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="volyx.error"
            p="1"
            borderRadius="md"
            _hover={{ bg: 'rgba(255,59,48,0.1)' }}
            onClick={clearProfileFile}
            aria-label="Remove selected image"
          >
            <X size={16} />
          </chakra.button>
        </Flex>
      ) : null}
    </Flex>
  );
}
